import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { Checkbox, TextField } from '@ui/inputs';
import { FlatButton } from '@ui/buttons';
import { Consequence, MicroLabel, PassiveCard } from '@features/scoreSpec/styles';
import { SourcePicker } from './SourcePicker';
import { ContextRail } from './ContextRail';
import { NameField } from './NameField';
import { PromptField } from './PromptField';
import { RewritesPanel } from './RewritesPanel';
import { DoneState } from './DoneState';
import { errorsFor } from './validate';
import { writeTarget } from '@features/scoreSpec/environment';
import { useDuplicateDraft } from './useDuplicateDraft';

// The simple form: pick a spec, change its name and prompt, get a new draft.
//
// It is the trimmed counterpart to the advanced editor at /advanced-score-spec, and the trim is
// the feature. That editor exposes the whole definition because someone authoring from scratch
// needs all of it; this one exposes three fields, because someone iterating on a question that
// already works needs exactly three and is better off unable to touch the rest.
//
// Spacing is on one scale, and it is the house one: 8px inside a component (a label to its own
// content, a hint under its field), 16px between components, 24px between sections. The steps are
// the sections; the fields, panels and rails inside them are the components.
//
// Two columns on purpose. The left one is everything the user changes; the right one is the
// contract that survives the change. They have to be legible at the same time — the failure this
// screen exists to prevent is a new prompt that asks for a different ANSWER SHAPE, and that is
// only obvious while the shape is visible.

// The shared Checkbox stacks its label ABOVE the box and wraps the box in a fixed 36px column, so a
// one-line opt-in comes out as two lines with the box adrift under the text. Re-laid as a row here
// rather than in the shared component, which every other caller lays out vertically on purpose.
const FeedToggle = styled.div`
  > div {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
  }

  /* The 36px column is what pushes the box away from the label it belongs to. */
  > div > div {
    width: auto;
  }

  label {
    cursor: pointer;
  }
`;

const EnvBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $production }) => ($production ? theme.themeColors.warning : theme.themeColors.lowContrastBorder)};
`;

const Step = ({ n, title, top = 24, children }) => (
  <Container top={top}>
    <MicroLabel>{`Step ${n}`}</MicroLabel>
    <Container top={8}>
      <Body5 bold>{title}</Body5>
    </Container>
    <Container top={16}>{children}</Container>
  </Container>
);

// `initialSourceId` is the deep link from /edit-score-spec/:specId — the fetch screen's "Edit this
// spec", where the record has already been read. Step 1 is then answered on arrival rather than
// asked again; the picker still renders, and changing it still discards the seeded form.
export const DuplicateSpecForm = ({ initialSourceId }) => {
  const draft = useDuplicateDraft(initialSourceId);
  const target = writeTarget();
  const descriptionErrors = errorsFor(draft.errors, 'description');
  const contractBlocking = (draft.contract && draft.contract.problems.blocking) || [];

  // `disabled` on the shared buttons is styling only — the click still fires — so the guard has
  // to live in the handler.
  const handleSubmit = () => {
    if (!draft.canSubmit) return;
    draft.submit();
  };

  if (draft.created) {
    return (
      <DoneState record={draft.created.record} changes={draft.created.changes} onStartAnother={draft.startAnother} />
    );
  }

  return (
    <div>
      <EnvBar $production={target.isProduction}>
        <Body7 bold>{`Writing to ${target.label}`}</Body7>
        <Body7 isLowContrast>{target.host}</Body7>
        <Body7 isLowContrast>· Dev/QA and production are separate stores; a spec created here exists only here.</Body7>
      </EnvBar>

      <Container top={24}>
        <Step n={1} title="Pick the spec to copy" top={0}>
          <SourcePicker
            sources={draft.sources || []}
            isLoading={draft.isLoadingSources}
            error={draft.sourcesError}
            onReload={draft.reloadSources}
            value={draft.sourceId}
            onSelect={draft.selectSource}
            canDuplicateSource={draft.canDuplicateSource}
          />
        </Step>
      </Container>

      {draft.isLoadingSource && (
        <Container top={24}>
          <Body5 isLowContrast>Loading the source spec…</Body5>
        </Container>
      )}

      {draft.sourceError && (
        <Container top={24}>
          <PassiveCard>
            <Body5>{draft.sourceError}</Body5>
            <Container top={8}>
              {/* The fix comes with the problem: a deleted source and a source that is the wrong
                  status both stop here, and only one of them is fixed by reloading the list. */}
              <Consequence $tone="bad">{draft.sourceErrorFix}</Consequence>
            </Container>
          </PassiveCard>
        </Container>
      )}

      {draft.definition && draft.isBlocked && (
        <Container top={24}>
          <PassiveCard>
            <Body5 bold>This spec cannot be duplicated here</Body5>
            <Container top={8}>
              <Consequence $tone="bad">{draft.preview.blocked}</Consequence>
            </Container>
          </PassiveCard>
        </Container>
      )}

      {draft.definition && !draft.isBlocked && (
        <div>
          <Step n={2} title="Change what the copy asks">
            <NameField
              value={draft.form.stem}
              onChange={(stem) => draft.setForm({ stem })}
              errors={draft.errors}
              specId={draft.specId}
              sourceIdentity={draft.sourceIdentity}
            />

            <Container top={16}>
              <TextField
                id="duplicate-description"
                label="Description"
                value={draft.form.description}
                onChange={(description) => draft.setForm({ description })}
                invalid={descriptionErrors.length > 0}
                hint={
                  descriptionErrors[0] ||
                  'Inherited from the source — rewrite it. It describes the behaviour, and it is the label the whole team reads in the list.'
                }
              />
            </Container>

            <Container top={16}>
              <PromptField
                value={draft.form.prompt}
                onChange={(prompt) => draft.setForm({ prompt })}
                errors={draft.errors}
                documentsKind={draft.documentsKind}
              />
            </Container>
          </Step>

          <Step n={3} title="Carried across untouched">
            <ContextRail definition={draft.definition} contract={draft.contract} />
          </Step>

          <Step n={4} title="Check what gets sent, then create the draft">
            {/* A re-cut is the one case where clearing the source's feed_name is the WRONG default: the
                prompt is unchanged, so the signal is the same and breaking the feed is the error. The
                form works this out rather than asking — see reCut.js. */}
            {draft.reCut && (
              <Container bottom={16}>
                <PassiveCard aria-label="Feed continuity">
                  <Body5 bold>The prompt is unchanged, so this is the same signal</Body5>
                  <Container top={8}>
                    <Body7 isLowContrast>
                      Only the way the answer is demanded has changed. A copy normally starts a new feed; this one does
                      not have to.
                    </Body7>
                  </Container>
                  <Container top={16}>
                    <FeedToggle>
                      <Checkbox
                        name="keep-feed"
                        value={draft.reCut.keepFeed}
                        label={`Keep publishing to ${draft.reCut.sourceId}’s feed`}
                        onChange={() => (next) => draft.reCut.setKeepFeed(next)}
                      />
                    </FeedToggle>
                  </Container>
                  {draft.reCut.keepFeed && (
                    <>
                      <Container top={8}>
                        <Consequence>{draft.reCut.note}</Consequence>
                      </Container>
                      <Container top={8}>
                        <Consequence $tone="warn">
                          Retire {draft.reCut.sourceId} BEFORE approving this copy. Feed names are unique across the
                          approved set and the check runs at approve, which cannot be retried — get the order wrong and
                          this spec is frozen holding a name it can never have.
                        </Consequence>
                      </Container>
                    </>
                  )}
                </PassiveCard>
              </Container>
            )}

            <RewritesPanel changes={draft.changes} spec={draft.preview.spec} />

            {/* Only problems this session introduced reach here — a source's own are subtracted, so
                the form does not start refusing specs it duplicates today. */}
            {contractBlocking.map((message) => (
              <Container top={16} key={message}>
                <Consequence $tone="bad">{message}</Consequence>
              </Container>
            ))}

            {draft.submitError && (
              <Container top={16}>
                <Consequence $tone="bad">{draft.submitError}</Consequence>
              </Container>
            )}

            <Container top={16} row verticallyCenter>
              <FlatButton onClick={handleSubmit} disabled={!draft.canSubmit}>
                {draft.isSubmitting ? 'Creating…' : 'Create draft'}
              </FlatButton>
              <Container left={16}>
                <Body7 isLowContrast>
                  {draft.errors.length + contractBlocking.length > 0
                    ? `${draft.errors.length + contractBlocking.length} thing${
                        draft.errors.length + contractBlocking.length === 1 ? '' : 's'
                      } to fix above.`
                    : `Creates ${draft.specId} as a draft. Drafts run nothing and can be deleted outright.`}
                </Body7>
              </Container>
            </Container>
          </Step>
        </div>
      )}
    </div>
  );
};
